import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  User,
  Bell,
  Palette,
  DatabaseZap,
  FileBarChart,
  TableProperties,
} from 'lucide-react';
import { ComprehensiveProfileEditor } from '../components/Settings/ComprehensiveProfileEditor';
import { NotificationSettingsTab } from '../components/Settings/NotificationSettingsTab';
import { AppearanceTab } from '../components/Settings/AppearanceTab';
import { AdminOperationsTab } from '../components/Settings/AdminOperationsTab';
import { PreCannedReportSettingsTab } from '../components/Settings/PreCannedReportSettingsTab';
import { MetricsCatalogTab } from '../components/Settings/MetricsCatalogTab';
import { useAuth } from '../contexts/AuthContext';
import { PRE_CANNED_REPORTS_ENABLED } from '../config/featureFlags';


export const SettingsSection: React.FC = () => {
  const location = useLocation();
  const { canAccessModule, isAdmin } = useAuth();
  const canManageSettings = canAccessModule('settings');
  const canUseAdminOperations = isAdmin();
  const [activeTab, setActiveTab] = useState('profile');

  // Handle URL parameters for tab switching
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams.get('tab');
    const validTabs = canManageSettings
      ? [
          'profile',
          'notifications',
          'appearance',
          ...(PRE_CANNED_REPORTS_ENABLED ? ['pre-canned-report'] : []),
          'metrics-catalog',
          ...(canUseAdminOperations ? ['cache-settings'] : []),
        ]
      : ['profile'];

    const normalizedTabParam = tabParam === 'admin' ? 'cache-settings' : tabParam;

    if (normalizedTabParam && validTabs.includes(normalizedTabParam)) {
      setActiveTab(normalizedTabParam);
      if (tabParam !== normalizedTabParam) {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', normalizedTabParam);
        window.history.replaceState({}, '', url.toString());
      }
    } else if (tabParam && !validTabs.includes(tabParam)) {
      setActiveTab('profile');
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'profile');
      window.history.replaceState({}, '', url.toString());
    }
  }, [canManageSettings, canUseAdminOperations, location.search]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tabId);
    window.history.pushState({}, '', url.toString());
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    ...(canManageSettings
      ? [
          { id: 'notifications', label: 'Notifications', icon: Bell },
          { id: 'appearance', label: 'Appearance', icon: Palette },
          ...(PRE_CANNED_REPORTS_ENABLED
            ? [{ id: 'pre-canned-report', label: 'Pre-Canned Report', icon: FileBarChart }]
            : []),
          { id: 'metrics-catalog', label: 'Metrics Catalog', icon: TableProperties },
          ...(canUseAdminOperations
            ? [{ id: 'cache-settings', label: 'Cache Settings', icon: DatabaseZap }]
            : []),
        ]
      : []),
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="page-header">Settings</h1>
        <p className="page-subtitle">
          Manage your account preferences and platform settings
        </p>
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="flex border-b border-light">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'text-primary-600 border-b-2 border-primary-600 bg-primary-50'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {activeTab === 'profile' && <ComprehensiveProfileEditor />}
          {canManageSettings && activeTab === 'notifications' && <NotificationSettingsTab />}
          {canManageSettings && activeTab === 'appearance' && <AppearanceTab />}
          {PRE_CANNED_REPORTS_ENABLED && canManageSettings && activeTab === 'pre-canned-report' && <PreCannedReportSettingsTab />}
          {canManageSettings && activeTab === 'metrics-catalog' && <MetricsCatalogTab />}
          {canManageSettings && canUseAdminOperations && activeTab === 'cache-settings' && <AdminOperationsTab />}
        </div>
      </div>
    </div>
  );
};
